import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { bookingByAdmin, updateBooking } from "../../apis/bookingApi";
import DataTable from "../../components/dashboard/Table2";
// import DataTable from "../../components/dashboard/Table";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "@tanstack/react-query";
import { manageBookingsQueryOptions } from "../../queryOptions/ManageBookingsOptions";
// import CalendarView from "./CalenderView";
import AddBookingForm from "./AddBooking";
import { getAllListings } from "../../apis/LaneCageApi";
import { toast } from "react-toastify";
import { FinalBooking } from "../../schemas/bookings";
import AdminCalendar from "../../components/calendar/Calendar";
import { CalendarEvent } from "../../schemas/calendar";
import { Listing } from "../../schemas/calendar";

function mapBookingsToEvents(bookings: FinalBooking[]): CalendarEvent[] {
  const filteredBookings = bookings.filter((booking) => (booking.bookingInfo.status.toLocaleLowerCase() !== "cancelled" && booking.bookingInfo.status.toLocaleLowerCase() !== "refunded"))
  return filteredBookings.map((booking) => ({
    id: booking.bookingInfo.bookingId,
    title: booking.bookingInfo.laneOrCage.name,
    description: booking.bookingInfo.specialRequests || '',
    startTime: new Date(booking.bookingInfo.startTime),
    endTime: new Date(booking.bookingInfo.endTime),
    listingId: booking.bookingInfo.laneOrCage.id,
    customerId: booking.bookingInfo.userDetails.email,
    customerName: booking.bookingInfo.userDetails.name,
    status: booking.bookingInfo.status.toLocaleLowerCase(),
    price: booking.bookingInfo.payment.totalAmount,
    color: "#7c3aed", // You can customize this or use a color from the listing
  })
);
}

const ManageBookings = () => {
  const [bookings, setBookings] = useState<FinalBooking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error] = useState<string | null>(null);
  const [searchTerm] = useState<string>("");
  const [filteredBookings, setFilteredBookings] = useState<FinalBooking[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [lanes, setLanes] = useState([]);
  const [isAddBookingOpen, setIsAddBookingOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, refetch } = useQuery(manageBookingsQueryOptions());
  useEffect(() => {
    if (data && data.bookings) {
      if (data.bookings.length > 0) {
        // Log all unique statuses in the data
        const allStatuses = new Set();
        data.bookings.forEach((booking: FinalBooking) => {
          const currentStatus = booking.bookingInfo.status || "unknown";
          allStatuses.add(currentStatus);
        });
      }
      setBookings(data.bookings);
      setFilteredBookings(data.bookings);
      setLoading(false);
    }
  }, [data]);

  const fetchLanes = async () => {
    try {
      const data = await getAllListings();
      setLanes(data.laneOrCages);
    } catch (error) {
      console.error("Error fetching lanes:", error);
    }
  };

  useEffect(() => {
    fetchLanes();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setLoading(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = bookings.filter(
        (booking) =>
          booking.bookingInfo.userDetails.name.toLowerCase().includes(lowercasedSearch) ||
          booking.bookingInfo.userDetails.email.toLowerCase().includes(lowercasedSearch) ||
          booking.bookingInfo.laneOrCage.name.toLowerCase().includes(lowercasedSearch) ||
          (booking.bookingInfo.status || "").toLowerCase().includes(lowercasedSearch)
      );
      setFilteredBookings(filtered);
    } else {
      setFilteredBookings(bookings);
    }
  }, [searchTerm, bookings]);

  const handleFilterClick = (status: string) => {
    if (activeFilter === status) {
      // If clicking the active filter, remove it
      setActiveFilter(null);
      setFilteredBookings(bookings);
    } else {
      // Apply new filter
      setActiveFilter(status);
      const filtered = bookings.filter((booking) => {
        // Use the direct status field from the backend response
        const currentStatus = booking.bookingInfo.status || "unknown";
        
        // Convert status to lowercase and handle different formats
        const normalizedStatus = status.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ');
        const normalizedCurrentStatus = currentStatus.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ');
        
        // Also try matching without spaces
        const normalizedStatusNoSpaces = status.toLowerCase().replace(/_/g, '').replace(/-/g, '');
        const normalizedCurrentStatusNoSpaces = currentStatus.toLowerCase().replace(/_/g, '').replace(/-/g, '');
        
        // Return true if either format matches
        return normalizedCurrentStatus === normalizedStatus || 
               normalizedCurrentStatusNoSpaces === normalizedStatusNoSpaces;
      });
      
      setFilteredBookings(filtered);
    }
  };

  // Modified tab change handler to refresh data when switching to Table View
  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTabValue(newValue);

    // If switching to Table View (tab index 1), refresh the data
    if (newValue === 1) {
      await handleRefresh();
    }
  };

  // Handle opening the Add Booking form
  const handleBooking = () => {
    setIsAddBookingOpen(true);
  };

  // Handle closing the Add Booking form
  const handleCloseAddBooking = () => {
    setIsAddBookingOpen(false);
  };

  const handleAddBooking = async (bookingData: any) => {
    try {
      const response = await bookingByAdmin(bookingData);
      const succeeded =
        response.success === true ||
        response.success === "true" ||
        response.status === "success" ||
        response.statusCode === 201;

      if (!succeeded) {
        throw new Error(
          response.data?.message || response.message || "Booking creation failed."
        );
      }
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || "Error creating booking. Please try again."
      );
    }
  };

  const handleBatchComplete = async (
    count: number,
    options?: { partial?: boolean }
  ) => {
    await refetch();
    if (options?.partial) return;
    toast.success(
      count === 1 ? "Booking created successfully!" : `Created ${count} bookings`,
      {
        position: "top-center",
        autoClose: 5000,
      }
    );
  };

  return (
    <Box sx={{ px: { xs: 0, md: 2 }, py: { xs: 2, md: 3 } }}>
      <Paper>
        {notification && (
          <Alert
            severity={notification.type}
            onClose={() => setNotification(null)}
            sx={{ mb: 2 }}
          >
            {notification.message}
          </Alert>
        )}
      </Paper>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, md: 3 },
          borderRadius: 2,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            mb: 3,
            gap: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold">
            Manage Bookings
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleBooking}
            >
              Add Booking
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                refreshing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filter by status:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {[
              "Confirmed",
              "Pay_At_Counter",
              "Cancelled",
              "Rescheduled",
              "Send_Invoice",
              "Invoice_Sent",
              "Refunded",
              "Group_On",
            ].map((status) => (
              <Chip
                key={status}
                label={status}
                clickable
                size="small"
                color={activeFilter === status ? "primary" : "default"}
                onClick={() => handleFilterClick(status)}
                variant={activeFilter === status ? "filled" : "outlined"}
              />
            ))}
          </Stack>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {filteredBookings.length}{" "}
              {filteredBookings.length === 1 ? "booking" : "bookings"} found
              {activeFilter && ` with status "${activeFilter}"`}
              {searchTerm && ` matching "${searchTerm}"`}
            </Typography>
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="booking tabs"
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ minHeight: 48 }}
          >
            <Tab label="Calendar View" />
            <Tab label="Table View" />
          </Tabs>
        </Box>

        <Box role="tabpanel" hidden={tabValue !== 0} sx={{ height: "100%" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <AdminCalendar events={mapBookingsToEvents(bookings)} listings={lanes as Listing[]} />
            // <CalendarView bookings={bookings} />
          )}
        </Box>
        <Box role="tabpanel" hidden={tabValue !== 1}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Refreshing booking data...</Typography>
            </Box>
          ) : (
            <DataTable
              data={filteredBookings}
              loading={loading}
              onStatusChange={async (
                id: string,
                newStatus: string,
                email?: string,
                listingName?: string,
                firstName?: string,
                lastName?: string,
                totalHours?: number,
                totalAmount?: number,
                bookingForDate?: string,
                startTime?: string,
                endTime?: string,
                laneOrCageId?: string,
                specialRequests?: string
              ) => {
                await updateBooking(
                  id,
                  newStatus.toLowerCase(),
                  email,
                  listingName,
                  firstName,
                  lastName,
                  totalHours,
                  totalAmount,
                  bookingForDate,
                  startTime,
                  endTime,
                  laneOrCageId,
                  specialRequests
                );
                // Refresh the bookings after status update
                refetch();
              }}
              onRefresh={async () => {
                await refetch();
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Add Booking Form Dialog */}
      <AddBookingForm
        open={isAddBookingOpen}
        onClose={handleCloseAddBooking}
        onAddBooking={handleAddBooking}
        onBatchComplete={handleBatchComplete}
        lanes={lanes}
      />
    </Box>
  );
};

export default ManageBookings;
